import { useState, useEffect, useRef } from 'react';
import { CaretLeft, CaretRight, Pause, Play } from '@phosphor-icons/react';
import useInView from '../hooks/useInView';

/**
 * Content-fitted carousel.
 * - Renders only the active slide (no fixed width forced on slides).
 * - Each slide takes its own natural size; carousel grows/shrinks to fit it.
 * - Smooth crossfade + horizontal slide animation between slides.
 * - Auto-rotate pauses permanently as soon as the user interacts (dot/arrow),
 *   so lingering slides like "Betaling"/"Examen" stay put. A tiny play/pause
 *   toggle lets the user resume auto-rotate.
 */
export default function CardCarousel({ slides = [], autoRotateMs = 0 }) {
    const [activeIdx, setActiveIdx] = useState(0);
    const [direction, setDirection] = useState(1);
    const [paused, setPaused] = useState(false);
    const intervalRef = useRef(null);
    const [containerRef, inView] = useInView({ threshold: 0.3 });

    const visibleSlides = slides.filter(s => s.visible !== false);
    const total = visibleSlides.length;

    useEffect(() => {
        if (activeIdx >= total) setActiveIdx(0);
    }, [total, activeIdx]);

    useEffect(() => {
        if (!autoRotateMs || total <= 1 || !inView || paused) return;
        intervalRef.current = setInterval(() => {
            setDirection(1);
            setActiveIdx(i => (i + 1) % total);
        }, autoRotateMs);
        return () => clearInterval(intervalRef.current);
    }, [autoRotateMs, total, activeIdx, inView, paused]);

    if (total === 0) return null;

    const goTo = (idx, dir = 1) => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setPaused(true);
        setDirection(dir);
        setActiveIdx(((idx % total) + total) % total);
    };

    const current = visibleSlides[activeIdx];
    const canAutoRotate = !!autoRotateMs && total > 1;

    return (
        <div ref={containerRef} className="relative inline-flex flex-col max-w-full" data-testid="card-carousel">
            {/* Slide stage — auto height, content-sized */}
            <div className="relative rounded-xl">
                <div
                    key={current.id || activeIdx}
                    data-testid={`carousel-slide-${current.id || activeIdx}`}
                >
                    {current.content}
                </div>
            </div>

            {total > 1 && (
                <>
                    {/* Floating prev/next buttons (positioned over the slide) */}
                    <button
                        type="button"
                        onClick={() => goTo(activeIdx - 1, -1)}
                        className="absolute left-1 top-1/2 -translate-y-[60%] z-10 w-9 h-9 rounded-full bg-[#1A202C]/80 hover:bg-[#1A202C] border border-[#4A5568] text-white flex items-center justify-center backdrop-blur-sm transition-all hover:scale-110"
                        data-testid="carousel-prev"
                        aria-label="Vorige"
                    >
                        <CaretLeft size={18} weight="bold" />
                    </button>
                    <button
                        type="button"
                        onClick={() => goTo(activeIdx + 1, 1)}
                        className="absolute right-1 top-1/2 -translate-y-[60%] z-10 w-9 h-9 rounded-full bg-[#1A202C]/80 hover:bg-[#1A202C] border border-[#4A5568] text-white flex items-center justify-center backdrop-blur-sm transition-all hover:scale-110"
                        data-testid="carousel-next"
                        aria-label="Volgende"
                    >
                        <CaretRight size={18} weight="bold" />
                    </button>

                    {/* Dots / labels */}
                    <div className="flex items-center justify-center gap-2 mt-3 flex-wrap" data-testid="carousel-dots">
                        {visibleSlides.map((s, i) => {
                            const isActive = i === activeIdx;
                            return (
                                <button
                                    key={s.id || i}
                                    onClick={() => goTo(i, i > activeIdx ? 1 : -1)}
                                    className={`group flex items-center gap-1.5 transition-all ${isActive ? 'pointer-events-none' : ''}`}
                                    data-testid={`carousel-dot-${i}`}
                                    aria-label={s.label || `Slide ${i + 1}`}
                                >
                                    <span
                                        className={`block rounded-full transition-all ${
                                            isActive
                                                ? 'w-6 h-2 bg-[#D4A537]'
                                                : 'w-2 h-2 bg-[#4A5568] group-hover:bg-gray-500'
                                        }`}
                                    />
                                    {s.label && (
                                        <span className={`text-[10px] uppercase tracking-wider transition-all ${isActive ? 'text-[#D4A537] font-semibold' : 'text-gray-500'}`}>
                                            {s.label}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        {canAutoRotate && (
                            <button
                                type="button"
                                onClick={() => setPaused(p => !p)}
                                className={`ml-1 w-6 h-6 rounded-full border flex items-center justify-center transition-all ${
                                    paused
                                        ? 'border-[#D4A537]/50 bg-[#D4A537]/10 text-[#D4A537] hover:bg-[#D4A537]/20'
                                        : 'border-[#4A5568] bg-[#1A202C] text-gray-400 hover:text-white hover:border-gray-500'
                                }`}
                                data-testid="carousel-play-pause"
                                aria-label={paused ? 'Auto-rotate hervatten' : 'Auto-rotate pauzeren'}
                                title={paused ? 'Auto-rotate hervatten' : 'Auto-rotate pauzeren'}
                            >
                                {paused ? <Play size={11} weight="fill" /> : <Pause size={11} weight="fill" />}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
